import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, ChevronLeft, Lock, ShoppingBag, Sparkles, Zap, Star, X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { formatDate, formatNumber } from '@/utils/formatDate';
import { catalogText } from '@/utils/catalog';

interface Reward {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  type: string;
  category: string;
  iconUrl: string | null;
  pointCost: number;
  stockLimit: number | null;
  stockRemaining: number | null;
  minLevel: number;
  displayOrder: number;
  canAfford: boolean;
  canRedeem: boolean;
  levelLocked: boolean;
  soldOut: boolean;
}

interface Redemption {
  id: string;
  pointsSpent: number;
  status: string;
  fulfilledAt: string | null;
  createdAt: string;
  reward: {
    name: string;
    slug: string;
    type: string;
    category: string;
    iconUrl: string | null;
  };
}

const CATEGORY_ICONS: Record<string, typeof Gift> = {
  MERCH: ShoppingBag,
  DIGITAL: Sparkles,
  FEATURE: Zap,
  EXPERIENCE: Star,
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  MERCH: { bg: 'bg-gold/5', text: 'text-gold', border: 'border-gold/20' },
  DIGITAL: { bg: 'bg-violet/5', text: 'text-violet', border: 'border-violet/20' },
  FEATURE: { bg: 'bg-steel/5', text: 'text-steel', border: 'border-steel/20' },
  EXPERIENCE: { bg: 'bg-forest-wash', text: 'text-forest', border: 'border-forest/20' },
};

export default function RewardsPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated, scout } = useAuthStore();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'catalog' | 'my-rewards'>('catalog');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [confirmReward, setConfirmReward] = useState<Reward | null>(null);
  const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  async function loadData() {
    try {
      const [catalogData, redemptionData] = await Promise.all([
        api.get<Reward[]>('/scout/rewards'),
        api.get<Redemption[]>('/scout/rewards/my-redemptions'),
      ]);
      setRewards(catalogData);
      setRedemptions(redemptionData);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  // Escape key to close modal
  useEffect(() => {
    if (!confirmReward) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmReward(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [confirmReward]);

  async function handleRedeem(reward: Reward) {
    setRedeemingId(reward.id);
    try {
      const result = await api.post<{ rewardName: string; pointsSpent: number; newCurrentXp: number }>(
        `/scout/rewards/${reward.id}/redeem`,
        {},
      );
      setRedeemSuccess(result.rewardName);
      setRedeemError(null);
      setConfirmReward(null);
      window.gtag?.('event', 'reward_redeemed', { reward_name: result.rewardName, points_spent: result.pointsSpent });
      // Reload data to reflect changes
      await loadData();
      // Auto-dismiss success after 3s
      setTimeout(() => setRedeemSuccess(null), 3000);
    } catch (err) {
      setConfirmReward(null);
      setRedeemError(err instanceof Error ? err.message : t('rewards.redeemFailed'));
      setTimeout(() => setRedeemError(null), 4000);
    } finally {
      setRedeemingId(null);
    }
  }

  const categories = Array.from(new Set(rewards.map((r) => r.category)));
  const filteredRewards = selectedCategory ? rewards.filter((r) => r.category === selectedCategory) : rewards;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gold/5 to-white pb-24">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-2 -ms-2 rounded-lg hover:bg-warm-faint">
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-section font-bold text-foreground">{t('rewards.title')}</h1>
          <p className="text-small text-muted-foreground">{t('rewards.subtitle')}</p>
        </div>
      </div>

      {/* XP Balance */}
      {scout && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-gradient-to-r from-gold/5 to-gold/10 border border-gold/20 flex items-center justify-between">
          <span className="text-small text-muted-foreground">{t('profile.spendableXp')}</span>
          <span className="text-section font-bold text-gold">{formatNumber(scout.currentXp)} XP</span>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 mb-4 flex gap-2">
        <button
          onClick={() => setActiveTab('catalog')}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'catalog' ? 'bg-foreground text-white' : 'bg-muted text-muted-foreground hover:bg-warm-faint'
          }`}
        >
          {t('rewards.catalog')}
        </button>
        <button
          onClick={() => setActiveTab('my-rewards')}
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'my-rewards'
              ? 'bg-foreground text-white'
              : 'bg-muted text-muted-foreground hover:bg-warm-faint'
          }`}
        >
          {t('rewards.myRewards')} {redemptions.length > 0 && `(${redemptions.length})`}
        </button>
      </div>

      {/* Success toast */}
      {redeemSuccess && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-forest-wash border border-forest/20 text-forest text-small font-medium text-center animate-fade-in">
          {t('rewards.redeemSuccess', { name: redeemSuccess })}
        </div>
      )}

      {/* Error toast */}
      {redeemError && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-critical/5 border border-critical/20 text-critical text-small font-medium text-center animate-fade-in">
          {redeemError}
        </div>
      )}

      {/* Load error */}
      {loadError && (
        <div className="mx-4 mb-4 p-3 rounded-xl bg-critical/5 border border-critical/20 text-critical text-small text-center">
          {t('rewards.loadError')}
        </div>
      )}

      {activeTab === 'catalog' ? (
        <>
          {/* Category filters */}
          <div className="px-4 mb-4 flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                !selectedCategory ? 'bg-foreground text-white' : 'bg-muted text-muted-foreground hover:bg-warm-faint'
              }`}
            >
              {t('rewards.all')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? 'bg-foreground text-white'
                    : 'bg-muted text-muted-foreground hover:bg-warm-faint'
                }`}
              >
                {t(`rewards.category.${cat}`)}
              </button>
            ))}
          </div>

          {/* Reward cards */}
          <div className="px-4 space-y-3">
            {filteredRewards.map((reward) => (
              <RewardCard key={reward.id} reward={reward} t={t} onRedeem={() => setConfirmReward(reward)} />
            ))}
          </div>

          {filteredRewards.length === 0 && (
            <div className="text-center py-12">
              <Gift className="w-12 h-12 text-steel mx-auto mb-3" />
              <p className="text-muted-foreground">{t('rewards.empty')}</p>
            </div>
          )}
        </>
      ) : (
        /* My Rewards tab */
        <div className="px-4 space-y-3">
          {redemptions.length === 0 ? (
            <div className="text-center py-12">
              <Gift className="w-12 h-12 text-steel mx-auto mb-3" />
              <p className="text-muted-foreground">{t('rewards.noRedemptions')}</p>
            </div>
          ) : (
            redemptions.map((r) => <RedemptionCard key={r.id} redemption={r} t={t} />)
          )}
        </div>
      )}

      {/* Confirm modal */}
      {confirmReward && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg bg-white rounded-t-2xl p-6 animate-slide-up">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-section font-bold text-foreground">{t('rewards.confirmTitle')}</h3>
              <button
                onClick={() => setConfirmReward(null)}
                className="p-1 rounded-lg hover:bg-warm-faint"
                aria-label={t('common.close')}
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="p-4 rounded-xl bg-warm-faint mb-4">
              <p className="font-semibold text-foreground">{confirmReward.name}</p>
              <p className="text-small text-muted-foreground mt-1">{confirmReward.description}</p>
              <p className="text-section font-bold text-gold mt-2">{confirmReward.pointCost} XP</p>
            </div>

            <p className="text-small text-muted-foreground mb-2">
              {t('rewards.confirmMessage', { name: confirmReward.name, cost: confirmReward.pointCost })}
            </p>
            {scout && (
              <p className="text-caption text-muted-foreground mb-4">
                {t('rewards.xpRemaining', { xp: Math.max(0, scout.currentXp - confirmReward.pointCost) })}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmReward(null)}
                className="flex-1 py-3 rounded-xl border border-warm-border text-foreground font-medium text-small"
              >
                {t('rewards.cancel')}
              </button>
              <button
                onClick={() => handleRedeem(confirmReward)}
                disabled={redeemingId === confirmReward.id}
                className="flex-1 py-3 rounded-xl bg-primary text-white font-medium text-small disabled:opacity-50"
              >
                {redeemingId === confirmReward.id ? t('rewards.redeeming') : t('rewards.confirmRedeem')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RewardCard({
  reward,
  t,
  onRedeem,
}: {
  reward: Reward;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onRedeem: () => void;
}) {
  const colors = CATEGORY_COLORS[reward.category] ?? CATEGORY_COLORS.MERCH!;
  const Icon = CATEGORY_ICONS[reward.category] ?? Gift;
  const isComingSoon = reward.type === 'VIRTUAL';

  return (
    <div
      className={`p-4 rounded-xl border ${colors.border} ${colors.bg} transition-all ${isComingSoon ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              {/* "Coming soon" shows once, in the action slot below (not also next to the name). */}
              <p className="font-semibold text-foreground text-small">
                {catalogText(reward.slug, 'name', reward.name)}
              </p>
              <p className="text-caption text-muted-foreground mt-0.5">
                {catalogText(reward.slug, 'description', reward.description)}
              </p>
            </div>
            <span className="text-sm font-bold text-gold whitespace-nowrap">{reward.pointCost} XP</span>
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-caption text-muted-foreground">
              {reward.stockLimit !== null && (
                <span>
                  {reward.soldOut
                    ? t('rewards.soldOut')
                    : t('rewards.stockLeft', { count: reward.stockRemaining ?? 0 })}
                </span>
              )}
              {reward.minLevel > 1 && (
                <span className={reward.levelLocked ? 'text-gold' : ''}>
                  {reward.levelLocked
                    ? t('rewards.levelRequired', { level: reward.minLevel })
                    : `${t('home.level', { level: reward.minLevel })}+`}
                </span>
              )}
            </div>

            {isComingSoon ? (
              <span className="px-3 py-1.5 rounded-lg bg-steel/10 text-steel text-caption font-medium">
                {t('rewards.comingSoon')}
              </span>
            ) : reward.soldOut ? (
              <span className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-caption font-medium">
                {t('rewards.soldOut')}
              </span>
            ) : reward.levelLocked ? (
              <span className="px-3 py-1.5 rounded-lg bg-gold/10 text-gold text-caption font-medium flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t('rewards.locked')}
              </span>
            ) : !reward.canAfford ? (
              <span className="px-3 py-1.5 rounded-lg bg-muted text-muted-foreground text-caption font-medium">
                {t('rewards.notEnoughXp')}
              </span>
            ) : (
              <button
                onClick={onRedeem}
                className="px-4 py-1.5 rounded-lg bg-primary text-white text-caption font-medium hover:bg-forest transition-colors"
              >
                {t('rewards.redeem')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RedemptionCard({
  redemption,
  t,
}: {
  redemption: Redemption;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const colors = CATEGORY_COLORS[redemption.reward.category] ?? CATEGORY_COLORS.MERCH!;
  const Icon = CATEGORY_ICONS[redemption.reward.category] ?? Gift;

  const statusColors: Record<string, string> = {
    PENDING: 'bg-gold/10 text-gold',
    FULFILLED: 'bg-forest-wash text-forest',
    CANCELLED: 'bg-critical/10 text-critical',
  };

  return (
    <div className="p-4 rounded-xl border border-warm-border bg-white">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
          <Icon className={`w-5 h-5 ${colors.text}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-foreground text-small">
              {catalogText(redemption.reward.slug, 'name', redemption.reward.name)}
            </p>
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${statusColors[redemption.status] ?? ''}`}>
              {t(`rewards.status.${redemption.status}`)}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-caption text-muted-foreground">
            <span>{redemption.pointsSpent} XP</span>
            <span>{formatDate(redemption.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
